import { supabase } from '../supabase';
import { normalizeLinkedInUrl } from '../utils/csvParser';
import { isUuid, parseScrapeJson } from '../utils/parseScrapeJson';

const SELECT_COLUMNS = 'id, candidate_id, linkedin_url, scraped_at, raw_apify, raw_brightdata';

const extractLinkedInSlug = (input) => {
  const normalized = normalizeLinkedInUrl(input);
  if (normalized) {
    const match = normalized.match(/\/in\/([^/?#]+)/i);
    if (match?.[1]) return decodeURIComponent(match[1]);
  }

  const rawMatch = String(input || '').match(/linkedin\.com\/in\/([^/?#]+)/i);
  return rawMatch?.[1] ? decodeURIComponent(rawMatch[1].trim()) : '';
};

const linkedInUrlVariants = (slug) => {
  const clean = String(slug || '').replace(/\/+$/, '');
  if (!clean) return [];
  return [
    `https://www.linkedin.com/in/${clean}`,
    `https://linkedin.com/in/${clean}`,
    `https://www.linkedin.com/in/${clean}/`,
    `https://linkedin.com/in/${clean}/`,
  ];
};

const mapScrapeRow = (row) => ({
  id: row.id,
  candidateId: row.candidate_id,
  linkedinUrl: row.linkedin_url,
  scrapedAt: row.scraped_at,
  rawApify: parseScrapeJson(row.raw_apify),
  rawBrightdata: parseScrapeJson(row.raw_brightdata),
});

/**
 * Look up scrape_results by candidate_id or LinkedIn URL.
 * Returns all matching rows, newest first.
 */
export const getScrapeResultsByQuery = async (query) => {
  const trimmed = String(query ?? '').trim();
  if (!trimmed) return [];

  let request = supabase
    .from('scrape_results')
    .select(SELECT_COLUMNS)
    .order('scraped_at', { ascending: false });

  if (isUuid(trimmed)) {
    request = request.eq('candidate_id', trimmed);
  } else {
    const slug = extractLinkedInSlug(trimmed);
    if (!slug) {
      const error = new Error('INVALID_QUERY');
      error.code = 'INVALID_QUERY';
      throw error;
    }

    const variants = linkedInUrlVariants(slug);
    const orParts = [
      `linkedin_url.ilike.%/in/${slug}%`,
      ...variants.map((url) => `linkedin_url.eq.${url}`),
    ];
    request = request.or(orParts.join(','));
  }

  const { data, error } = await request;
  if (error) throw error;

  return (data ?? []).map(mapScrapeRow);
};
